import PropTypes from "prop-types"
import React, { createContext, useCallback, useEffect, useState } from "react"
import ContactBoatsTab from "src/main/Contact/ContactBoatsTab"
import ContactsTab from "src/main/Contact/ContactsTab"
import MarinaInviteModal from "src/main/Contact/MarinaInviteModal"

import Button from "src/components/Button"
import CollapsibleBar from "src/components/CollapsibleBar"
import InsightCard from "src/components/InsightCard"
import InsightSummary from "src/components/InsightSummary"
import Tabs from "src/components/Tabs"

import { useTracker } from "src/hooks/use_tracker"

import { updateUrlQueryParams } from "src/utils/url/parsing/marina"

export const ContactsContext = createContext(null)

const Contacts = ({
  canCreate,
  canExport,
  contractGroups,
  exportUrl,
  insights,
  longestLoa,
  newContactUrl,
  marinaInvitePath,
  marinaMcomId,
  marinaId,
  marinaName,
}) => {
  const urlParams = new URLSearchParams(window.location.search)
  const activeTab = urlParams.get("active_tab")
  const page = urlParams.get("page")
  const [status, setStatus] = useState(urlParams.get("status") || "active")
  const [currentTab, setCurrentTab] = useState(0)

  const [currentContactPage, setCurrentContactPage] = useState(
    page && activeTab === "captains" ? parseInt(page) : 1
  )
  const [contactSearchQuery, setContactSearchQuery] = useState(
    activeTab === "captains" ? urlParams.get("search") || "" : ""
  )
  const [contactSortDirection, setContactSortDirection] = useState(
    activeTab === "captains" ? urlParams.get("sort_direction") : null
  )
  const [contactSortKey, setContactSortKey] = useState(
    activeTab === "captains" ? urlParams.get("sort_key") : null
  )
  const [isMarinaInviteModalOpen, setIsMarinaInviteModalOpen] = useState(false)

  const tracker = useTracker()
  const trackingProperties = {
    marina_id: marinaId,
    marina_mcom_id: marinaMcomId,
    marina_name: marinaName,
  }
  const tabName = useCallback((tab) => {
    switch (tab) {
      case 0:
        return "boats"
      case 1:
        return "captains"
      default:
        return "captains"
    }
  }, [])

  const handleTabChange = (tab) => {
    setCurrentTab(tab)
    updateUrlQueryParams({ active_tab: tabName(tab) })
    tracker.trackEvent(`contacts:${tabName(tab)}_tab_pressed`)
  }

  useEffect(() => {
    if (activeTab === "boats") {
      setCurrentTab(0)
      // Only set active_tab without wiping useDatatable-managed params
      const url = new URL(window.location.href)
      url.searchParams.set("active_tab", tabName(0))
      window.history.replaceState(null, null, url.toString())
    } else {
      setCurrentTab(1)
      updateUrlQueryParams({
        active_tab: tabName(1),
        page: currentContactPage,
        search: contactSearchQuery,
        sort_direction: contactSortDirection,
        sort_key: contactSortKey,
        status,
      })
    }
  }, [
    activeTab,
    tabName,
    contactSearchQuery,
    currentContactPage,
    contactSortDirection,
    contactSortKey,
    status,
  ])

  return (
    <ContactsContext.Provider
      value={{
        canCreate,
        canExport,
        contactSearchQuery,
        contactSortDirection,
        contactSortKey,
        contractGroups,
        currentContactPage,
        currentTab,
        exportUrl,
        longestLoa: longestLoa / 12,
        marinaInvitePath,
        newContactUrl,
        setContactSearchQuery,
        setContactSortDirection,
        setContactSortKey,
        setCurrentContactPage,
        setCurrentTab,
        setStatus,
        status,
        trackingProperties,
      }}
    >
      <div className="bg-white px-2 lg:px-6">
        {insights && (
          <div className="mb-4 print:hidden">
            <CollapsibleBar
              title={
                <span className="text-blue-800">
                  Insights <i className="fa-kit fa-stars" />
                </span>
              }
              ariaLabel="Toggle Insights panel"
              accent="bg-gradient-to-r from-blue-500 via-blue-900 to-blue-700"
              summary={
                <InsightSummary
                  items={[
                    { value: insights.contacts, label: "customers" },
                    { value: insights.boats, label: "boats" },
                    { value: insights.averageLoa, label: "avg LOA" },
                    { value: insights.homeports, label: "homeports" },
                  ]}
                />
              }
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <InsightCard
                  icon="fa-md-group"
                  title="Customers"
                  value={insights.contacts}
                  description={`${insights.usersPct}% of boaters use Dockwa`}
                />
                <InsightCard
                  icon="fa-md-directions-boat"
                  title="Boats"
                  value={insights.boats}
                  description={`${insights.powerBoatPct}% power boats, ${insights.sailPct}% sailboats`}
                />
                <InsightCard
                  icon="fa-md-tag"
                  title="Average LOA"
                  value={insights.averageLoa}
                  description={`${insights.boatsOver35Pct}% of boats are over 35'`}
                />
                <InsightCard
                  icon="fa-waves"
                  title="Homeport Marina"
                  value={insights.homeports}
                  description={`${insights.homeports} added you as homeport`}
                />
              </div>
            </CollapsibleBar>
          </div>
        )}
        <Tabs
          selectedIndex={currentTab}
          onChange={(tab) => handleTabChange(tab)}
        >
          <div className="flex w-full flex-col justify-between lg:flex-row print:hidden">
            <div className="flex justify-center border-b lg:border-none">
              <Tabs.TabList>
                <Tabs.Tab title="Boats" />
                <Tabs.Tab title="Captains" />
              </Tabs.TabList>
            </div>
            <Button
              variant="secondary"
              onClick={() => {
                setIsMarinaInviteModalOpen(true)
                tracker.trackEvent(
                  "marina_invite:link_modal_button_pressed",
                  trackingProperties
                )
              }}
            >
              Customer Invite Link
            </Button>
          </div>
          <hr className="mt-2 print:hidden" />
          <Tabs.Panels>
            <Tabs.Panel>
              <ContactBoatsTab />
            </Tabs.Panel>
            <Tabs.Panel>
              <ContactsTab />
            </Tabs.Panel>
          </Tabs.Panels>
        </Tabs>
        <MarinaInviteModal
          isOpen={isMarinaInviteModalOpen}
          setIsOpen={setIsMarinaInviteModalOpen}
        />
      </div>
    </ContactsContext.Provider>
  )
}

Contacts.propTypes = {
  canCreate: PropTypes.bool.isRequired,
  canExport: PropTypes.bool.isRequired,
  insights: PropTypes.shape({
    contacts: PropTypes.string,
    usersPct: PropTypes.number,
    boats: PropTypes.string,
    powerBoatPct: PropTypes.number,
    sailPct: PropTypes.number,
    averageLoa: PropTypes.string,
    boatsOver35Pct: PropTypes.number,
    homeports: PropTypes.string,
  }),
  longestLoa: PropTypes.number.isRequired,
  newContactUrl: PropTypes.string.isRequired,
  exportUrl: PropTypes.string.isRequired,
  contractGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      encodedId: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  marinaInvitePath: PropTypes.string,
  marinaMcomId: PropTypes.string,
  marinaId: PropTypes.number.isRequired,
  marinaName: PropTypes.string.isRequired,
}

export default Contacts
